// Catalog.ID Client — Recovery Kit Creator
// ============================================================================
// Two-stage UI for creating a Recovery Kit (Addendum §7/§8):
//   1. distribution-pattern preset + per-sheet purposes and additional copies
//      (+ supersecret entry when the caller doesn't already hold it)
//   2. ONE print job for the sheets + the encrypted key backup download.
//
// Sheet output is print-only (registration-restructure M8; Addendum §6.1/§17 as
// amended by §10): the old rule was "one PDF per sheet", because a single FILE
// holding a quorum of shares is itself a recovery artifact. A print job is not
// a durable file — the operative control is physical page separation, so the
// sheets go out as pages of one document, one sheet per page, the same builder
// the registration document set uses. The encrypted key backup stays a
// download: it is ciphertext, and the fire drill needs that file.
//
// Used inside a settings modal (supersecret re-prompted and verified, M12
// pattern) and the org workspace's Recovery Kit pane; the registration wizards
// build their kit inline and print it as part of their own document set.

import { createElement } from '@catalog-shell';
import { createSupersecretTextarea } from './supersecret-textarea';
import { downloadBlob } from './pdf-loader';
import { printBooklet } from './booklet/booklet';
import {
    buildRecoveryCardPages, buildRecoveryCardCopySheets, type RecoveryCardContact,
} from './booklet/recovery-card-page';
import { cardTypeLetter, formatVersion, type AccountHeaderData } from './booklet/shared';
import { createRecoveryKitCardFields } from './recovery-kit-card-fields';
import type { RecoveryKitPresetKind } from './recovery-kit-presets';
import { buildRecoveryKit, type RecoveryKit } from '../crypto/recovery-kit';
import { mintRecoveryKitEntry, type RecoveryKitEntryData } from '../crypto/recovery-kit-entry';
import type { StoredKitEntry } from './recovery-kit-store';
import { serializeBundle, bundleFilename, type BundleBlob } from '../crypto/bundle';
import * as accountApi from '../api/account';
import type { KeyBag } from '../crypto/keybag';

export interface RecoveryKitSource {
    /** Resolve the material the kit is built from. When `supersecret` is
     *  passed the caller must verify it (settings does so by decrypting the
     *  server key bag); when the caller already holds it (wizard), ignore
     *  the argument. Throw with a user-facing message on failure. */
    resolve(supersecretInput: string | null): Promise<{
        supersecret: string;
        bag: KeyBag;
        publicKeys: { keyId: string; algorithm: string; publicKey: string; keyIndex?: number; status?: string }[];
    }>;
    /** True when the UI must collect the supersecret (settings); false when
     *  the caller already holds it (registration wizard). */
    needsSupersecretInput: boolean;
}

export interface RecoveryKitCreatorConfig {
    cid: string;
    /** Which distribution presets to offer — the account's own kind, not the
     *  kind of the session driving the ceremony (an org kit is created from a
     *  delegate's session). */
    kind: RecoveryKitPresetKind;
    /** Account-header line on the printed sheets ('Personal', 'Organization',
     *  …). Defaults to 'Account' when the caller has no better label. */
    accountTypeLabel?: string;
    /** Label above the supersecret box. Defaults to the personal wording; an
     *  organization ceremony must say WHOSE supersecret it wants, since the
     *  delegate driving it holds one of their own. */
    supersecretPrompt?: string;
    /** Verified email/phone printed on every sheet so a custodian can tell
     *  whose sheet it is. Personal accounts; organizations have none. */
    contact?: RecoveryCardContact;
    source: RecoveryKitSource;
    /** Where the kit record is written. Defaults to the SESSION account's kit;
     *  an organization kit belongs to the org, not to the delegate whose
     *  session is driving the ceremony, so that flow passes its own. */
    recordKit?: (req: accountApi.PutRecoveryKitRequest) => Promise<void>;
    /**
     * Persist the freshly minted SET as the account's `recovery_kit` key-bag
     * entry and hand back what the bag ACTUALLY holds afterwards
     * (registration-restructure §16.6). The kit is built from THAT, never from
     * the bytes minted here: the personal write is last-writer-wins, so the
     * server's copy is the account's set — and it is the copy every later
     * reprint will read.
     *
     * `components/recovery-kit-store.ts` has the two implementations (personal
     * single-entry add; organization whole-bag PUT).
     */
    persistKitEntry: (params: {
        entry: RecoveryKitEntryData; supersecret: string;
    }) => Promise<StoredKitEntry>;
    /**
     * Fetch the account's blobs for the kit's bundle (cid-encrypted-storage
     * §4.2). Omit where the account has none to fetch: an ORGANIZATION kit is
     * created from a DELEGATE's session, and `/api/account/blobs` answers for
     * the session's account — collecting there would file the delegate's blobs
     * in the organization's bundle. Absent means the bundle carries `[]`.
     */
    collectBlobs?: () => Promise<BundleBlob[]>;
    /** Called after the kit is built and the server record is written. */
    onCreated?: (kit: RecoveryKit) => void;
}

export function createRecoveryKitCreator(config: RecoveryKitCreatorConfig): HTMLElement {
    const container = createElement('div', { className: 'cid-recovery-kit-creator' });
    renderSetupStage(container, config);
    return container;
}

// ── Stage 1: preset + labels/copies (+ supersecret) ─────────────────────────

function renderSetupStage(container: HTMLElement, config: RecoveryKitCreatorConfig): void {
    container.innerHTML = '';

    const fields = createRecoveryKitCardFields({ kind: config.kind, idPrefix: 'kit-creator' });
    container.appendChild(fields.element);

    let ss: ReturnType<typeof createSupersecretTextarea> | null = null;
    if (config.source.needsSupersecretInput) {
        const ssField = createElement('div', { className: 'cid-auth-field' });
        ssField.appendChild(createElement('label', {
            className: 'cid-wizard-text',
            textContent: config.supersecretPrompt
                ?? 'Supersecret (verified against your account, then split across the sheets):',
        }));
        ss = createSupersecretTextarea();
        ssField.appendChild(ss.element);
        container.appendChild(ssField);
    }

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    container.appendChild(errorEl);

    const createBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Create Recovery Kit',
    }) as HTMLButtonElement;
    if (ss) {
        createBtn.disabled = true;
        ss.onValidityChange(valid => { createBtn.disabled = !valid; });
    }
    container.appendChild(createBtn);

    createBtn.addEventListener('click', async () => {
        errorEl.classList.remove('cid-pane-error--visible');
        createBtn.setAttribute('disabled', '');
        createBtn.textContent = 'Verifying supersecret...';
        try {
            const material = await config.source.resolve(ss ? ss.getValue() : null);

            const { preset, labels, extraCopies } = fields.read();

            // Round 9 (§16.6): the SET is stored in the key bag before anything
            // is printed, so every sheet of it can be printed again later. A
            // reissue adds the next generation and revokes the one it
            // supersedes in the same ceremony.
            createBtn.textContent = 'Storing the recovery set on your account...';
            const stored = await config.persistKitEntry({
                entry: mintRecoveryKitEntry({
                    thresholdK: preset.profile.thresholdK,
                    totalSharesN: preset.profile.totalSharesN,
                    labels,
                }),
                supersecret: material.supersecret,
            });

            createBtn.textContent = 'Splitting supersecret & encrypting backup...';
            const kit = await buildRecoveryKit({
                entry: stored.entry,
                cid: config.cid,
                supersecret: material.supersecret,
                cardExtraCopies: extraCopies.length === stored.entry.totalSharesN
                    ? extraCopies : undefined,
                // The bag AS WRITTEN — the kit entry rides in the bundle the
                // sheets reference, so a bundle restore brings the set back too.
                bag: stored.bag,
                publicKeys: material.publicKeys,
                blobs: config.collectBlobs ? await config.collectBlobs() : [],
            });

            createBtn.textContent = 'Recording kit on the server...';
            let serverRecordError: string | null = null;
            try {
                const recordKit = config.recordKit ?? accountApi.putRecoveryKit;
                await recordKit({
                    recoverySetId: kit.recoverySetId,
                    thresholdK: kit.thresholdK,
                    totalSharesN: kit.totalSharesN,
                    bundleFingerprint: kit.bundle.bundle_fingerprint,
                });
            } catch (err) {
                // The kit itself is complete and valid — the server record only
                // powers reminders. Surface but do not block.
                serverRecordError = err instanceof Error ? err.message : 'Could not record the kit on the server';
            }

            config.onCreated?.(kit);
            renderDownloadStage(container, config, kit, serverRecordError);
        } catch (err) {
            createBtn.removeAttribute('disabled');
            createBtn.textContent = 'Create Recovery Kit';
            errorEl.textContent = err instanceof Error ? err.message : 'Could not create the Recovery Kit';
            errorEl.classList.add('cid-pane-error--visible');
        }
    });
}

// ── Stage 2: print the sheets, download the backup ───────────────────────────

function renderDownloadStage(
    container: HTMLElement,
    config: RecoveryKitCreatorConfig,
    kit: RecoveryKit,
    serverRecordError: string | null,
): void {
    container.innerHTML = '';

    container.appendChild(createElement('h4', {
        className: 'cid-wizard-heading',
        textContent: 'Recovery Kit created — now print and distribute',
    }));

    if (serverRecordError) {
        container.appendChild(createElement('div', {
            className: 'cid-pane-error cid-pane-error--visible',
            textContent: `Note: the kit could not be recorded on the server (${serverRecordError}). `
                + 'The kit itself is complete; annual-test reminders will not be available.',
    }));
    }

    const letters = kit.cards
        .map(c => cardTypeLetter(c.payload.shareIndex) + (c.extraCopies > 0 ? ` ×${c.extraCopies + 1}` : ''))
        .join(', ');
    const pageCount = kit.cards.reduce((n, c) => n + 1 + c.extraCopies, 0);
    container.appendChild(createElement('p', {
        className: 'cid-wizard-warning',
        textContent: `One print job, ${pageCount} pages — sheets ${letters}, one per page. Separate the `
            + `pages as they come off the printer and hand each to its intended holder: any ${kit.thresholdK} `
            + 'of them together reconstruct your supersecret, so they must not stay together. Send this to a '
            + 'physical printer — a "Save as PDF" destination would leave the whole quorum in one file on '
            + 'this computer.',
    }));

    const list = createElement('div', { className: 'cid-credential-display' });
    const errorEl = createElement('div', { className: 'cid-pane-error' });

    const printRow = createElement('div', { className: 'cid-credential-actions' });
    const printBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-credential-action',
        textContent: 'Print recovery sheets',
    });
    printBtn.addEventListener('click', async () => {
        errorEl.classList.remove('cid-pane-error--visible');
        printBtn.setAttribute('disabled', '');
        try {
            const header = cardHeader(config);
            await printBooklet(
                buildRecoveryCardPages(header, kit, config.contact),
                buildRecoveryCardCopySheets(header, kit, config.contact),
            );
            printBtn.classList.add('cs-btn--yes');
        } catch (err) {
            errorEl.textContent = err instanceof Error ? err.message : 'Print failed';
            errorEl.classList.add('cid-pane-error--visible');
        } finally {
            printBtn.removeAttribute('disabled');
        }
    });
    printRow.appendChild(printBtn);
    list.appendChild(printRow);

    const bundleRow = createElement('div', { className: 'cid-credential-actions' });
    const bundleBtn = createElement('button', {
        className: 'cs-btn cs-btn--default cid-credential-action',
        textContent: 'Download encrypted key backup (bundle)',
    });
    bundleBtn.addEventListener('click', () => {
        downloadBlob(
            new Blob([serializeBundle(kit.bundle)], { type: 'application/json' }),
            bundleFilename(config.cid, kit.bundle),
        );
        bundleBtn.classList.add('cs-btn--yes');
    });
    bundleRow.appendChild(bundleBtn);
    list.appendChild(bundleRow);

    container.appendChild(list);
    container.appendChild(errorEl);

    container.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Each sheet page explains itself to whoever holds it and names the other sheets they '
            + 'would have to find — never where those are: write the coordinator\'s name on every sheet, and '
            + 'the full whereabouts only on the coordinator\'s own. The encrypted key backup is ciphertext '
            + 'and is safe to copy anywhere — '
            + 'the more copies, the better; the annual fire drill needs it. '
            + 'Test the kit at least once a year from Settings.',
    }));
}

/** The account header the printed sheets carry — the same one the registration
 *  document set prints, so a sheet reissued from Settings is indistinguishable
 *  from one printed at registration. */
function cardHeader(config: RecoveryKitCreatorConfig): AccountHeaderData {
    return {
        cid: config.cid,
        accountTypeLabel: config.accountTypeLabel ?? 'Account',
        version: formatVersion(new Date()),
    };
}
